import { useLocalStorage } from "@uidotdev/usehooks";

export type ItemsViewMode = "rows" | "tiles";
export type TileColumns = 3 | 4 | 5 | 6;

export interface ItemsViewPreferences {
  mode: ItemsViewMode;
  columns: TileColumns;
}

// "storage" = the Storage page's Items view, the only consumer today.
export const ITEMS_VIEW_STORAGE_KEY = "storage-items-view";

export const TILE_COLUMN_OPTIONS: readonly TileColumns[] = [3, 4, 5, 6];

export const DEFAULT_ITEMS_VIEW_PREFERENCES: ItemsViewPreferences = {
  mode: "rows",
  columns: 4,
};

// Stored values come from localStorage and can be anything; normalize
// strictly (no coercion) and fail open to the defaults.
export const normalizeItemsViewMode = (raw: unknown): ItemsViewMode =>
  raw === "tiles" ? "tiles" : "rows";

export const normalizeTileColumns = (raw: unknown): TileColumns =>
  TILE_COLUMN_OPTIONS.includes(raw as TileColumns)
    ? (raw as TileColumns)
    : DEFAULT_ITEMS_VIEW_PREFERENCES.columns;

export const normalizeItemsViewPreferences = (
  raw: unknown,
): ItemsViewPreferences => {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return { ...DEFAULT_ITEMS_VIEW_PREFERENCES };
  }

  const record = raw as Partial<Record<keyof ItemsViewPreferences, unknown>>;

  return {
    mode: normalizeItemsViewMode(record.mode),
    columns: normalizeTileColumns(record.columns),
  };
};

type SetItemsViewPreferences = (patch: Partial<ItemsViewPreferences>) => void;

type ReturnItemsViewPreferences = [
  ItemsViewPreferences,
  SetItemsViewPreferences,
];

const useItemsViewPreferences = (): ReturnItemsViewPreferences => {
  const [stored, setStored] = useLocalStorage<ItemsViewPreferences>(
    ITEMS_VIEW_STORAGE_KEY,
    DEFAULT_ITEMS_VIEW_PREFERENCES,
  );

  const preferences = normalizeItemsViewPreferences(stored);

  const setPreferences: SetItemsViewPreferences = (patch) => {
    setStored(normalizeItemsViewPreferences({ ...preferences, ...patch }));
  };

  return [preferences, setPreferences];
};

export default useItemsViewPreferences;
